import React from 'react';
import { Form, TimeInput, Date } from '@weezevent/nacre';
import Styles from './dateTimeInput.module.css';
import moment from 'moment/moment';

/** This component is exclusively external controlled
 * Input = date string utc
 * Output = date string utc and moment object
 */
export function DateTimeInput({
    label = null,
    timeLabel,
    dateLabel,
    onChange,
    value = null,
    dateProps = {},
    timeProps = { step: 15 },
    locale = 'en-GB',
    timezone = 'UTC',
    error = null,
    returnFormat = null,
    className = '',
    defaultMinute = 0,
    defaultHour = 0,
    disabled = false,
    placeholder
}) {
    const internalDate = React.useMemo(() => {
        return value
            ? moment.tz(
                  moment
                      .utc(value, moment.ISO_8601)
                      .tz(timezone)
                      .format('YYYY-MM-DD HH:mm'),
                  timezone
              )
            : null;
    }, [value, timezone]);

    const changeDate = React.useCallback(
        dateString => {
            if (dateString === null) {
                // avoid clear with keyboard
                onChange(null, null);
                return;
            }

            const date = dateString.split('-');
            let currentDate = internalDate
                ? moment.tz(internalDate.format('YYYY-MM-DD HH:mm'), timezone)
                : moment
                      .tz(new Date(), timezone)
                      .set('hour', defaultHour)
                      .set('minute', defaultMinute)
                      .set('second', 0);
            currentDate.set('year', date[0]);
            // on set month, jan = 0, dec = 11
            currentDate.set('month', date[1] - 1);
            currentDate.set('date', date[2]);
            let currentDateUtc = moment(currentDate).utc();
            onChange(currentDateUtc.format(returnFormat), currentDateUtc);
        },
        [internalDate, timezone, onChange, returnFormat, defaultMinute, defaultHour]
    );

    const changeHours = React.useCallback(
        timeString => {
            let _timeString = timeString;
            if (timeString === null || timeString === '') {
                // avoid clear with keyboard
                _timeString = `${defaultHour}:${defaultMinute}`;
            }

            const date = _timeString.split(':');
            let currentDate = internalDate
                ? moment.tz(internalDate.format('YYYY-MM-DD HH:mm'), timezone)
                : moment
                      .tz(new Date(), timezone)
                      .set('hour', defaultHour)
                      .set('minute', defaultMinute)
                      .set('second', 0);
            currentDate.set('hour', date[0]);
            currentDate.set('minute', date[1]);
            let currentDateUtc = moment(currentDate).utc();
            onChange(currentDateUtc.format(returnFormat), currentDateUtc);
        },
        [internalDate, timezone, onChange, returnFormat, defaultMinute, defaultHour]
    );

    return (
        <div className={`${Styles['date-time-input']} ${className}`}>
            {label && <Form.Label>{label}</Form.Label>}
            <div className={Styles['date-container']}>
                <Form.Field className={Styles['date-form-field']}>
                    {dateLabel && <Form.Label>{dateLabel}</Form.Label>}
                    <Date.Input
                        {...dateProps}
                        errors={Boolean(error)}
                        value={internalDate ? internalDate.format('YYYY-MM-DD') : null}
                        locale={locale}
                        onChange={date => changeDate(date)}
                        returnFormat={'YYYY-MM-DD'}
                        disabled={disabled}
                        placeholder={placeholder}
                    />
                </Form.Field>
                <Form.Field>
                    {timeLabel && <Form.Label>{timeLabel}</Form.Label>}
                    <TimeInput
                        {...timeProps}
                        errors={Boolean(error)}
                        onChange={time => changeHours(time)}
                        value={internalDate ? internalDate.format('HH:mm') : null}
                        disabled={disabled}
                    />
                </Form.Field>
            </div>
            {error && <Form.FieldErrors errors={[error || null]} />}
        </div>
    );
}
